'use client';

import { useMemo, useRef, useState } from 'react';
import { molarMass } from '@/lib/browser-molar-mass';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

const EXAMPLES = ['H2O', 'C6H12O6', 'NaCl', 'Ca(OH)2', 'CuSO4·5H2O', 'H2SO4'];

export default function BrowserMolarMassCalculator() {
  const [formula, setFormula] = useState('C6H12O6');
  const reported = useRef(false);

  const { result, error } = useMemo(() => {
    try {
      return { result: molarMass(formula), error: null };
    } catch (e) {
      return { result: null, error: e instanceof Error ? e.message : 'Check the formula.' };
    }
  }, [formula]);

  function update(value: string) {
    if (!reported.current) {
      reported.current = true;
      void consumeToolUsage('molecular-weight-calculator', '').catch(() => undefined);
    }
    setFormula(value);
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Chemistry</span>
        <h2 className="tool-live-title">Molecular weight calculator</h2>
        <p className="tool-live-subtitle">
          Type a chemical formula to get its molar mass in g/mol and the mass share of each element.
          Groups, brackets and hydrates work.
        </p>
      </div>
      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field tool-live-field-full">
            <span>Chemical formula</span>
            <input
              autoCapitalize="off"
              onChange={(e) => update(e.target.value)}
              spellCheck={false}
              type="text"
              value={formula}
            />
          </label>
          <div className="tool-live-actions">
            {EXAMPLES.map((ex) => (
              <button
                className="button button-secondary"
                key={ex}
                onClick={() => update(ex)}
                type="button"
              >
                {ex}
              </button>
            ))}
          </div>
          {error ? <p className="status-message status-error">{error}</p> : null}
        </section>
        <section className="tool-live-preview">
          {result ? (
            <>
              <div className="tool-live-stats">
                <p className="tool-live-title">{result.mass.toFixed(3)} g/mol</p>
                <p className="tool-live-subtitle">Molar mass of {formula.trim()}</p>
              </div>
              <div className="tool-html-meta-grid">
                {result.parts.map((p) => (
                  <div className="tool-live-field" key={p.symbol}>
                    <span>
                      {p.name} ({p.symbol}) × {p.count}
                    </span>
                    <input
                      readOnly
                      type="text"
                      value={`${p.mass.toFixed(3)} g/mol · ${(p.share * 100).toFixed(2)}%`}
                    />
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </section>
      </div>
    </div>
  );
}
